import { toast } from 'sonner';
import React from 'react';
import { LogoSVG } from '../../../ui/Icons';

type ToastPromiseOptions<T = any> = {
  title?: string;
  loadingMessage?: string;
  successMessage?: string | ((result: T) => string);
  errorPermissionMessage?: string;
  errorFailedMessage?: string;
};

type BirdToastProps = {
  title?: string;
  message: string;
  type?: 'success' | 'error' | 'loading' | 'info' | 'warning';
};

function BirdToast({ title, message, type = 'success' }: BirdToastProps) {
  const bg =
    type === 'success'
      ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500'
      : type === 'error'
      ? 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500'
      : type === 'info'
      ? 'bg-gradient-to-r from-sky-50 to-blue-50 border-l-4 border-sky-500'
      : type === 'warning'
      ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-500'
      : 'bg-gradient-to-r from-cyan-50 to-teal-50 border-l-4 border-cyan-500';

  return (
    <div
      className={`flex items-center min-w-[400px] max-w-[90vw]  justify-between p-4 gap-4 rounded-lg shadow-lg  transition-all duration-300 hover:shadow-xl ${bg}`}
      style={{ boxSizing: 'border-box' }}
    >
      <div className={`flex-shrink-0 animate-bounce-slow`}>
        <LogoSVG />
      </div>
      <div className="min-w-0 flex-1">
        {title && (
          <div className="text-lg font-gilroy-bold text-slate-900 mb-1.5 tracking-tight leading-tight">
            {title}
          </div>
        )}
        <div className="text-[15px] font-gilroy-regular text-slate-700 leading-relaxed">
          {message}
        </div>
      </div>
    </div>
  );
}

const toastBaseOptions = {
  richColors: false,
  icon: null,
  style: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    boxShadow: 'none'
  },
  className: 'animate-slide-in'
};

export const birdToast = {
  success: (message: string, title?: string) => {
    return toast.custom(
      () => <BirdToast title={title} message={message} type="success" />,
      toastBaseOptions
    );
  },
  error: (message: string, title?: string) => {
    return toast.custom(
      () => <BirdToast title={title} message={message} type="error" />,
      toastBaseOptions
    );
  },
  info: (message: string, title?: string) => {
    return toast.custom(
      () => <BirdToast title={title} message={message} type="info" />,
      toastBaseOptions
    );
  },
  warning: (message: string, title?: string) => {
    return toast.custom(
      () => <BirdToast title={title} message={message} type="warning" />,
      toastBaseOptions
    );
  },
  loading: (message: string, title?: string) => {
    return toast.custom(
      () => <BirdToast title={title} message={message} type="loading" />,
      toastBaseOptions
    );
  },
  promise: <T,>(promise: Promise<T>, opts: ToastPromiseOptions<T>) => {
    return toast.promise(promise, {
      ...toastBaseOptions,
      loading: (
        <BirdToast
          title={opts.title}
          message={opts.loadingMessage || 'Loading...'}
          type="loading"
        />
      ),
      success: (res: T) => (
        <BirdToast
          title={opts.title}
          message={
            typeof opts.successMessage === 'function'
              ? opts.successMessage(res)
              : opts.successMessage || 'Done'
          }
          type="success"
        />
      ),
      error: (err: any) => {
        const msg =
          err && (err.status === 401 || err.status === 403)
            ? opts.errorPermissionMessage || 'Permission denied'
            : opts.errorFailedMessage || 'Action failed';
        return <BirdToast message={msg} type="error" />;
      }
    });
  }
};
